/** @jsxImportSource @emotion/react */
import React, { FC, memo, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Clear } from '@mui/icons-material';
import { css, Grow, IconButton, TextField } from '@mui/material';
import { MAX_COMMENT_SIZE } from 'pages/Checkout/utils';
import { TXT_ORDER_COMMENT, TXT_ORDER_COMMENT_MAX_SIZE } from 'translations';
import { Props } from './types';

const CheckoutComment: FC<Props> = ({ value, onChange, disabled, methods }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  useImperativeHandle(methods, () => ({
    focus: () => {
      inputRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus({ preventScroll: true });
    },
  }));

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const handleClear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  return (
    <TextField
      fullWidth
      variant="outlined"
      multiline
      minRows={2}
      label={t(TXT_ORDER_COMMENT)}
      inputRef={inputRef}
      value={value}
      onChange={handleChange}
      disabled={disabled}
      error={value.length > MAX_COMMENT_SIZE}
      InputLabelProps={{
        // The floated label and the gap cut in the outline are two separate nodes rendering the
        // same text at independently-computed sizes (the label is 16px scaled by 0.75, the
        // outline's legend is a real 12px). Where the webview scales them differently — Telegram's
        // font-size setting, iOS text auto-sizing — the gap comes out narrower than the label and
        // the border draws straight through it. Painting the label's own background makes it opaque
        // regardless of how wide the gap ends up.
        sx: {
          '&.MuiInputLabel-shrink': {
            px: 0.5,
            ml: -0.5,
            backgroundColor: 'background.default',
          },
        },
      }}
      {...(!disabled && {
        helperText: t(TXT_ORDER_COMMENT_MAX_SIZE, {
          count: value.length,
          maxCount: MAX_COMMENT_SIZE,
        }),
      })}
      {...(!disabled && {
        InputProps: {
          endAdornment: (
            <Grow in={!!value.length} timeout={80}>
              <IconButton
                color="primary"
                size="small"
                onClick={handleClear}
                css={css`
                  align-self: flex-start;
                `}
              >
                <Clear />
              </IconButton>
            </Grow>
          ),
        },
      })}
    />
  );
};

export default memo(CheckoutComment);
